import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { TrendingUp } from 'lucide-react';
import type { DviSignalData } from '@/hooks/useDviSignals';

interface DviApprovalTrendsChartProps {
  signals: DviSignalData | null | undefined;
  isLoading: boolean;
}

export function DviApprovalTrendsChart({ signals, isLoading }: DviApprovalTrendsChartProps) {
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-medium">Approval Trends</CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-48 w-full" />
        </CardContent>
      </Card>
    );
  }

  const conditionMetrics = signals?.by_condition ?? {};

  // Build simple bar data from condition metrics
  const conditions = ['green', 'yellow', 'red'] as const;
  const data = conditions.map(condition => ({
    condition,
    total: conditionMetrics[condition]?.total_findings ?? 0,
    approvalRate: Math.round((conditionMetrics[condition]?.customer_approval_rate ?? 0) * 100),
    overcallRate: Math.round((conditionMetrics[condition]?.overcall_rate ?? 0) * 100),
  }));

  const maxTotal = Math.max(...data.map(d => d.total), 1);

  const conditionColors = {
    green: 'bg-green-500',
    yellow: 'bg-yellow-500',
    red: 'bg-red-500',
  };

  const conditionLabels = {
    green: 'Good Condition',
    yellow: 'Monitor',
    red: 'Urgent',
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <TrendingUp className="h-4 w-4" />
          Approval Trends by Condition
        </CardTitle>
        <CardDescription>
          Customer approval rates for each condition level
        </CardDescription>
      </CardHeader>
      <CardContent>
        {data.every(d => d.total === 0) ? (
          <div className="text-center py-8 text-muted-foreground">
            No inspection data available yet
          </div>
        ) : (
          <div className="space-y-4">
            {data.map(item => (
              <div key={item.condition} className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="font-medium">{conditionLabels[item.condition]}</span>
                  <span className="text-muted-foreground">
                    {item.approvalRate}% approved • {item.total} findings
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex-1 bg-muted rounded-full h-3 overflow-hidden">
                    <div 
                      className={`h-full ${conditionColors[item.condition]} transition-all`}
                      style={{ width: `${(item.total / maxTotal) * 100}%` }}
                    />
                  </div>
                  {item.overcallRate > 20 && (
                    <span className="text-xs text-orange-600 whitespace-nowrap">
                      {item.overcallRate}% overcall
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
